import type { GameState } from '../types/game'
import { useGameStore } from '../store/gameStore'
import { createRoundEndTestScenario, createRound4EndTestScenario } from './testScenarios'

/**
 * Debug mode options for testing specific game states
 */
export type DebugMode = 'none' | 'round1to2' | 'round4end'

/**
 * Debug mode configuration for UI display
 */
export const DEBUG_MODES = [
  { value: 'none' as const, label: 'Normal Game', description: 'Start from beginning with full deck' },
  { value: 'round1to2' as const, label: 'Round 1→2 Transition', description: 'Skip to end of round 1 (one card away)' },
  { value: 'round4end' as const, label: 'End Game (Round 4)', description: 'Skip to final round end game testing' },
] as const

/**
 * Isolated debug game setup - keeps debug logic out of main files
 *
 * Call this from App.tsx when debug mode is selected.
 * This function loads a pre-configured game state for testing.
 *
 * @param mode - The debug scenario to load
 * @param playerCount - Number of players (3, 4, or 5)
 * @param seed - Optional seed for deterministic scenarios (omit for random)
 */
export function loadDebugMode(
  mode: DebugMode,
  playerCount: 3 | 4 | 5,
  seed?: number
): void {
  if (mode === 'none') {
    console.warn('[debugGameSetup] loadDebugMode called with mode=none, ignoring')
    return
  }

  console.log('[debugGameSetup] Loading debug mode:', mode, 'for', playerCount, 'players')

  // Select the appropriate scenario function
  const scenarioFn = mode === 'round1to2'
    ? createRoundEndTestScenario
    : createRound4EndTestScenario

  // Use provided seed or generate random seed for this session
  const actualSeed = seed ?? Date.now()
  console.log('[debugGameSetup] Using seed:', actualSeed, '(use this for reproducibility)')

  // Generate the game state
  const gameState = scenarioFn(playerCount, 0, actualSeed)

  // Log scenario details for verification
  console.log('[debugGameSetup] Scenario loaded:', {
    mode,
    playerCount,
    round: gameState.round.roundNumber,
    phase: gameState.round.phase.type,
    humanHand: gameState.players[0].hand.map(c => ({ artist: c.artist, auctionType: c.auctionType })),
    humanMoney: gameState.players[0].money,
    humanPaintings: gameState.players[0].purchases?.length || 0,
    cardsPlayedPerArtist: gameState.round.cardsPlayedPerArtist,
  })

  // Update store directly (isolated from gameStore debug logic)
  useGameStore.getState().setGameStateDirectly(gameState)
}
